import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'

type RadioTokens = {
  border: string
  checkedBorder: string
  /** CSS var - painted via radial-gradient, which can't take a token name. */
  dot: string
  disabledText: string
  focusRing: string
  label: string
}

const tokens: Record<ColorScheme, RadioTokens> = {
  dark: {
    border: 'border.strong',
    checkedBorder: 'interactive.radio-on',
    dot: tokenVar('colors.interactive.radio-on'),
    disabledText: 'text.tertiary',
    focusRing: 'interactive.focus-ring',
    label: 'text.primary',
  },
  light: {
    border: 'light.border.strong',
    checkedBorder: 'light.interactive.radio-on',
    dot: tokenVar('colors.light.interactive.radio-on'),
    disabledText: 'light.text.tertiary',
    focusRing: 'light.interactive.focus-ring',
    label: 'light.text.primary',
  },
}

export const radioGroupStyles = (
  direction: 'column' | 'row'
): SystemStyleObject => ({
  display: 'flex',
  flexDirection: direction,
  gap: '8px',
})

export const radioRootStyles = (disabled: boolean): SystemStyleObject => ({
  alignItems: 'center',
  cursor: disabled ? 'default' : 'pointer',
  display: 'inline-flex',
  gap: '8px',
  minW: 0,
})

export const radioInputStyles = (
  colorScheme: ColorScheme,
  disabled: boolean
): SystemStyleObject => {
  const t = tokens[colorScheme]

  return {
    appearance: 'none',
    bgColor: 'transparent',
    border: '1.5px solid',
    borderColor: t.border,
    borderRadius: 'full',
    cursor: disabled ? 'default' : 'pointer',
    flexShrink: 0,
    // Compact 14px indicator — the spec's 20px atom overwhelms dense panel
    // rows (product decision); colors keep the spec's radio-on binding.
    h: '14px',
    m: 0,
    w: '14px',
    _checked: {
      borderColor: t.checkedBorder,
      backgroundImage: `radial-gradient(circle, ${t.dot} 3px, transparent 3.5px)`,
    },
    _disabled: {
      cursor: 'default',
      opacity: 0.55,
    },
    _focusVisible: {
      outline: '2px solid',
      outlineColor: t.focusRing,
      outlineOffset: '2px',
    },
  }
}

export const radioLabelStyles = (
  colorScheme: ColorScheme,
  disabled: boolean
): SystemStyleObject => ({
  color: disabled
    ? tokens[colorScheme].disabledText
    : tokens[colorScheme].label,
  m: 0,
  minW: 0,
  // text/primary per the Radio Button spec; Caption size (not the spec's
  // Body) to match the compact indicator in dense panels.
  textStyle: 'caption',
})
